import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, 
  Search, 
  Truck,
  Phone,
  Mail,
  MapPin,
  MoreVertical,
  Edit,
  Trash2,
  MessageCircle
} from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SuppliersPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_name: '',
    whatsapp: '',
    email: '',
    address: '',
    notes: ''
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/suppliers`, {
        credentials: 'include'
      });
      if (response.ok) {
        setSuppliers(await response.json());
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    
    setSaving(true);
    try {
      const url = editingSupplier 
        ? `${API_URL}/api/suppliers/${editingSupplier.supplier_id}`
        : `${API_URL}/api/suppliers`;
      
      // Clean form data: convert empty strings to null for optional fields
      const cleanedData = {
        name: formData.name,
        contact_name: formData.contact_name || null,
        whatsapp: formData.whatsapp || null,
        email: formData.email || null,
        address: formData.address || null,
        notes: formData.notes || null
      };
      
      const response = await fetch(url, {
        method: editingSupplier ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(cleanedData)
      });

      // Parse the JSON response
      let data;
      try {
        data = await response.json();
      } catch {
        data = {};
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al guardar');
      }

      toast.success(editingSupplier ? 'Proveedor actualizado' : 'Proveedor creado');
      setShowForm(false);
      setEditingSupplier(null);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (supplierId) => {
    if (!confirm('¿Estás seguro de eliminar este proveedor?')) return;

    try {
      const response = await fetch(`${API_URL}/api/suppliers/${supplierId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Error al eliminar');

      toast.success('Proveedor eliminado');
      fetchSuppliers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      contact_name: supplier.contact_name || '',
      whatsapp: supplier.whatsapp || '',
      email: supplier.email || '',
      address: supplier.address || '',
      notes: supplier.notes || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_name: '',
      whatsapp: '',
      email: '',
      address: '',
      notes: ''
    });
  };

  const openWhatsApp = (phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  const filteredSuppliers = suppliers.filter(supplier => 
    supplier.name.toLowerCase().includes(search.toLowerCase()) ||
    supplier.contact_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="animate-fade-in" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Proveedores</h1>
          <p className="text-slate-500">{suppliers.length} proveedor{suppliers.length !== 1 ? 'es' : ''}</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-[#1E3A5F] hover:bg-[#162e4d]"
          data-testid="add-supplier-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Proveedor
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Buscar proveedores..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-suppliers"
        />
      </div>

      {/* Suppliers List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      ) : filteredSuppliers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredSuppliers.map((supplier) => (
            <div
              key={supplier.supplier_id}
              className="card-base card-hover p-4"
              data-testid={`supplier-${supplier.supplier_id}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <Truck className="w-6 h-6 text-indigo-600" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 mb-1">{supplier.name}</h3>
                  
                  {supplier.contact_name && (
                    <p className="text-sm text-slate-600 mb-2">Contacto: {supplier.contact_name}</p>
                  )}
                  
                  <div className="flex flex-wrap gap-2">
                    {supplier.whatsapp && (
                      <button 
                        onClick={() => openWhatsApp(supplier.whatsapp)}
                        className="flex items-center gap-1 text-sm text-[#25D366] hover:underline"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                        {supplier.whatsapp}
                      </button>
                    )}
                    {supplier.email && (
                      <a 
                        href={`mailto:${supplier.email}`}
                        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
                      >
                        <Mail className="w-3.5 h-3.5" />
                        {supplier.email}
                      </a>
                    )}
                  </div>
                  
                  {supplier.address && (
                    <p className="flex items-center gap-1 text-sm text-slate-500 mt-2">
                      <MapPin className="w-3.5 h-3.5" />
                      {supplier.address}
                    </p>
                  )}
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {supplier.whatsapp && (
                      <DropdownMenuItem onClick={() => openWhatsApp(supplier.whatsapp)}>
                        <MessageCircle className="w-4 h-4 mr-2 text-[#25D366]" />
                        Enviar WhatsApp
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => handleEdit(supplier)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleDelete(supplier.supplier_id)}
                      className="text-red-600"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <Truck className="empty-state-icon" />
          <p className="text-slate-500 mb-4">
            {search ? 'No se encontraron proveedores' : 'No tienes proveedores registrados'}
          </p>
          {!search && (
            <Button onClick={() => setShowForm(true)} className="bg-[#1E3A5F]">
              <Plus className="w-4 h-4 mr-2" />
              Agregar primer proveedor
            </Button>
          )}
        </div>
      )}

      {/* Supplier Form Modal */}
      <Dialog open={showForm} onOpenChange={(open) => {
        if (!open) {
          setShowForm(false);
          setEditingSupplier(null);
          resetForm();
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingSupplier ? 'Editar Proveedor' : 'Nuevo Proveedor'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre de la empresa *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="supplier-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="contact_name">Nombre de contacto</Label>
              <Input
                id="contact_name"
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="whatsapp">WhatsApp</Label>
                <Input
                  id="whatsapp"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                  placeholder="+52 123 456 7890"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="address">Dirección</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-[#1E3A5F]" 
                data-testid="save-supplier-btn"
                disabled={saving}
              >
                {saving ? 'Guardando...' : editingSupplier ? 'Guardar Cambios' : 'Crear Proveedor'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
